import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Row, Col, Input, Button, Table, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { textCapitalize } from "../../Helpers/Utils";
import JsBarcode from "jsbarcode";
import Select from "react-select";

const ProductList = () => {

  const dispatch = useDispatch(), navigate = useNavigate();
  const user = useSelector(s => s.customer.customer);
  const loading = useSelector(state => state.loading.loading);
  const [priceTagOpen, setPriceTagOpen] = useState(false);
  const [priceTagData, setPriceTagData] = useState(null);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /* VARIANT MODAL */
  const [variantOpen, setVariantOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  const [variant, setVariant] = useState({
    id: null,
    name: "",
    price: "",
    purchase_price: "",
    discount_type: null,
    discount_value: "",
    gst_rate: "",
    quantity: "",
    low_stock_limit: ""
  });

  const discountTypes = [
    { value: "flat", label: "Flat" },
    { value: "percentage", label: "Percentage" }
  ];

  const limit = 10;

  const renderBarcode = (id, value) => {
    if (!value) return;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        JsBarcode(el, value, {
          format: "CODE128",
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 14
        });
      }
    }, 50);
  };


  /* LOAD PRODUCTS */
  const load = async () => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/products/search.php?outlet_id=${user?.outlet_id}&q=${search}`
      });
      res?.success ? setRows(res.data || []) : toast.error(res?.message);
    } catch {
      toast.error("Network error");
    }
    dispatch(loadingFalse());
  };

  useEffect(() => { load(); }, [search]);

  /* PAGINATION */
  const total = rows.length;
  const start = (page - 1) * limit;
  const pagedRows = rows.slice(start, start + limit);

  /* STOCK COLOR */
  const stockColor = (qty, low = 10) => {
    if (qty <= low) return "danger";
    if (qty <= low * 2) return "warning";
    return "success";
  };

  /* SAVE VARIANT */
  const saveVariant = async () => {

    if (!variant.name.trim()) return toast.error("Variant name required");
    if (!variant.price) return toast.error("Price required");
    if (!variant.purchase_price) return toast.error("Purchase price required");

    if (Number(variant.price) <= Number(variant.purchase_price))
      return toast.error("Selling price must be greater than purchase price");

    if (!variant.discount_type) return toast.error("Discount type required");
    if (variant.discount_value === "") return toast.error("Discount value required");
    if (!variant.gst_rate) return toast.error("GST % required");
    if (variant.quantity === "") return toast.error("Quantity required");
    if (variant.low_stock_limit === "") return toast.error("Low stock limit required");

    dispatch(loadingTrue());

    const payload = {
      product_id: currentProduct.product_id,
      variant_id: variant.id,
      name: variant.name,
      price: Number(variant.price),
      purchase_price: Number(variant.purchase_price),
      discount_type: variant.discount_type.value,
      discount_value: Number(variant.discount_value),
      gst_rate: Number(variant.gst_rate),
      quantity: Number(variant.quantity),
      low_stock_limit: Number(variant.low_stock_limit)
    };

    try {
      const res = await Handler({
        method: "post",
        url: variant.id
          ? "/products/variant/update.php"
          : "/products/variant/add.php",
        data: payload
      });

      if (res?.success) {
        toast.success("Variant saved");
        setVariantOpen(false);
        load();
      } else toast.error(res?.message);
    } catch {
      toast.error("Network error");
    }

    dispatch(loadingFalse());
  };

  /* PRICE TAG */
  const downloadPriceTag = async (productId) => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/products/price_tag_view.php?product_id=${productId}`
      });

      if (!res?.success) {
        toast.error("Price tag not available");
        return;
      }

      setPriceTagData(res.data);
      setPriceTagOpen(true);

    } catch {
      toast.error("Network error");
    }
    dispatch(loadingFalse());
  };

  const handlePrint = () => {
    if (!priceTagData) return;

    const win = window.open("", "_blank", "width=400,height=300");

    const productTagHTML = priceTagData.product ? `
    <div className="print-card">
      <div className="pt-name">${priceTagData.product.name}</div>
      ${priceTagData.product.sku ? `<div className="pt-meta">SKU: ${priceTagData.product.sku}</div>` : ""}
      ${priceTagData.product.size ? `<div className="pt-meta">Size: ${priceTagData.product.size}</div>` : ""}
      <div className="pt-price">${priceTagData.product.price_tag.final_price}</div>
      <svg id="p-barcode"></svg>
      <div className="pt-barcode-text">${priceTagData.product.price_tag.barcode}</div>
    </div>
  ` : "";

    const variantTagsHTML = priceTagData.variants?.map(v => `
    <div className="print-card">
      <div className="pt-name">${v.name}</div>
      ${v.sku ? `<div className="pt-meta">SKU: ${v.sku}</div>` : ""}
      ${v.size ? `<div className="pt-meta">Size: ${v.size}</div>` : ""}
      <div className="pt-price">${v.price_tag.final_price}</div>
      <svg id="v-barcode-${v.variant_id}"></svg>
      <div className="pt-barcode-text">${v.price_tag.barcode}</div>
    </div>
  `).join("") || "";

    win.document.open();
    win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Price Tag Print</title>
        <meta charset="utf-8" />
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
          }

          .print-card {
            width: 50mm;
            height: 25mm;
            padding: 2mm;
            box-sizing: border-box;
            text-align: center;
            page-break-after: always;
          }

          .pt-name {
            font-size: 9px;
            font-weight: 700;
            line-height: 1.1;
            text-transform : capitalize;
          }

          .pt-meta {
            font-size: 7px;
            line-height: 1.1;
          }

          .pt-price {
            font-size: 14px;
            font-weight: 800;
            margin: 2px 0;
          }

          .pt-barcode-text {
            font-size: 7px;
            letter-spacing: 1px;
          }

          svg {
            width: 100%;
            height: 12mm;
          }
        </style>
      </head>

      <body>
        ${productTagHTML}
        ${variantTagsHTML}
      </body>
    </html>
  `);
    win.document.close();

    win.onload = () => {
      // PRODUCT BARCODE
      if (priceTagData?.product?.price_tag?.barcode) {
        JsBarcode(
          win.document.getElementById("p-barcode"),
          priceTagData.product.price_tag.barcode,
          { format: "CODE128", width: 1.5, height: 30, displayValue: false }
        );
      }

      // VARIANT BARCODES
      priceTagData?.variants?.forEach(v => {
        const el = win.document.getElementById(`v-barcode-${v.variant_id}`);
        if (el) {
          JsBarcode(
            el,
            v.price_tag.barcode,
            { format: "CODE128", width: 1.5, height: 30, displayValue: false }
          );
        }
      });

      setTimeout(() => {
        win.focus();
        win.print();
        win.close();
      }, 300);
    };
  };




  useEffect(() => {
    if (!priceTagOpen || !priceTagData) return;

    if (priceTagData?.product?.price_tag?.barcode) {
      renderBarcode(
        "product-barcode",
        priceTagData.product.price_tag.barcode
      );
    }

    priceTagData?.variants?.forEach(v => {
      renderBarcode(
        `variant-barcode-${v.variant_id}`,
        v.price_tag.barcode
      );
    });

  }, [priceTagOpen, priceTagData]);


  return (
    <>
      <Row className="mb-3">
        <Col><h5 className="fw-bold">Products</h5></Col>
      </Row>

      <Card className="mb-3">
        <CardBody>
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search product / SKU / barcode"
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Table responsive className="modern-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product ID</th>
                <th>Product</th>
                <th>Meta</th>
                <th className="text-end">Stock</th>
                <th className="text-end">Price</th>
                <th className="text-end">Variants</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>

              {pagedRows.map((p, i) => (
                <React.Fragment key={i}>

                  <tr>
                    <td>{start + i + 1}</td>
                    <td>{p.product_id}</td>

                    <td>
                      <b>{p.name}</b>
                      {p.discount?.value > 0 &&
                        <div>
                          <Badge color="info" className="mt-1">
                            {p.discount.type === "percentage"
                              ? `${p.discount.value}% OFF`
                              : `₹${p.discount.value} OFF`}
                          </Badge>
                        </div>}
                    </td>

                    <td className="small text-muted">
                      {p.meta?.sku && <div>SKU: {p.meta.sku}</div>}
                      {p.meta?.barcode && <div>Barcode: {p.meta.barcode}</div>}
                      {p.meta?.size && <div>Size: {p.meta.size}</div>}
                      {p.category_name && <div>Category: {p.category_name}</div>}
                      {p.sub_category_name && <div>Sub: {p.sub_category_name}</div>}
                    </td>

                    <td className="text-end">
                      <Badge color={stockColor(p.quantity, p.low_stock_limit)}>
                        {p.quantity}
                      </Badge>
                    </td>

                    <td className="text-end">₹ {Number(p.price).toFixed(2)}</td>

                    <td className="text-end">
                      <Badge color="secondary">{p.variants?.length || 0}</Badge>
                    </td>

                    <td className="text-end">
                      {/* EDIT */}
                      <Button disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => navigate(`/products-edit/${p.product_id}`)}>
                        <i className="bx bx-edit"></i>
                      </Button>

                      {/* PRICE TAG */}
                      <Button  disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => downloadPriceTag(p.product_id)}>
                        <i className="bx bx-purchase-tag"></i>
                      </Button>

                      {/* VIEW */}
                      <Button  disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => navigate(`/products-view/${p.product_id}`)}>
                        <i className="bx bx-show"></i>
                      </Button>

                      {/* ADD VARIANT */}
                      <Button  disabled={loading} size="sm" color="light"
                        onClick={() => {
                          setCurrentProduct(p);
                          setVariant({
                            id: null,
                            name: "",
                            price: "",
                            purchase_price: "",
                            discount_type: null,
                            discount_value: "",
                            gst_rate: "",
                            quantity: "",
                            low_stock_limit: ""
                          });
                          setVariantOpen(true);
                        }}>
                        <i className="bx bx-plus"></i>
                      </Button>
                    </td>
                  </tr>

                  { p?.variants?.map((v, vi) => (
                    <tr key={vi}>
                      <td></td>
                      <td>Variant - {v.variant_id}</td>
                      <td className="ps-4">↳ {v.name}</td>
                      <td className="small text-muted">
                        GST {v.gst_rate}% | Disc {v.discount?.value}
                      </td>
                      <td className="text-end">
                        <Badge color={stockColor(v.quantity, v.low_stock_limit)}>
                          {v.quantity}
                        </Badge>
                      </td>
                      <td className="text-end">₹ {Number(v.price).toFixed(2)}</td>
                      <td></td>
                      <td className="text-end">
                        <Button
                          disabled={loading}
                          size="sm" color="light"
                          onClick={() => {
                            setCurrentProduct(p);
                            setVariant({
                              id: v.variant_id,
                              name: v.name,
                              price: v.price,
                              purchase_price: v.purchase_price,
                              discount_type: discountTypes.find(d => d.value === v.discount?.type),
                              discount_value: v.discount?.value,
                              gst_rate: v.gst_rate,
                              quantity: v.quantity,
                              low_stock_limit: v.low_stock_limit
                            });
                            setVariantOpen(true);
                          }}>
                          <i className="bx bx-edit"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}

                </React.Fragment>
              ))}

            </tbody>
          </Table>

          <Pagination
            page={page}
            total={total}
            limit={limit}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => p + 1)}
          />
        </CardBody>
      </Card>

      {/* VARIANT MODAL */}
      <Modal isOpen={variantOpen} toggle={() => setVariantOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setVariantOpen(false)}>
          Variant – {textCapitalize(currentProduct?.name)}
        </ModalHeader>

        <ModalBody>
          <Row className="g-3">
            <Col md={6}><Input placeholder="Name" value={variant.name}
              onChange={e => setVariant({ ...variant, name: e.target.value })} /></Col>
            <Col md={6}><Input placeholder="Price" value={variant.price}
              onChange={e => setVariant({ ...variant, price: e.target.value })} /></Col>
            <Col md={6}><Input placeholder="Purchase Price" value={variant.purchase_price}
              onChange={e => setVariant({ ...variant, purchase_price: e.target.value })} /></Col>
            <Col md={6}><Select options={discountTypes} value={variant.discount_type}
              onChange={v => setVariant({ ...variant, discount_type: v })} /></Col>
            <Col md={6}><Input placeholder="Discount Value" value={variant.discount_value}
              onChange={e => setVariant({ ...variant, discount_value: e.target.value })} /></Col>
            <Col md={6}><Input placeholder="GST %" value={variant.gst_rate}
              onChange={e => setVariant({ ...variant, gst_rate: e.target.value })} /></Col>
            <Col md={6}><Input placeholder="Quantity" value={variant.quantity}
              onChange={e => setVariant({ ...variant, quantity: e.target.value })} /></Col>
            <Col md={6}><Input placeholder="Low Stock Limit" value={variant.low_stock_limit}
              onChange={e => setVariant({ ...variant, low_stock_limit: e.target.value })} /></Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button disabled={loading} color="light" onClick={() => setVariantOpen(false)}>Cancel</Button>
          <Button disabled={loading} color="success" onClick={saveVariant}>Save Variant</Button>
        </ModalFooter>
      </Modal>
      {/* PRICE TAG MODAL */}
      <Modal isOpen={priceTagOpen} toggle={() => setPriceTagOpen(false)} centered size="md">
        <ModalHeader toggle={() => setPriceTagOpen(false)}>
          Price Tags Preview
        </ModalHeader>

        <ModalBody className="print-area text-capitalize">
          {/* ================= MAIN PRODUCT TAG ================= */}
          {priceTagData?.product && (
            <div className="price-tag-card mb-3">
              <div className="price-tag-header">PRODUCT</div>

              <div className="price-tag-body">
                <div className="pt-name">{priceTagData.product.name}</div>

                {priceTagData.product.sku && (
                  <div className="pt-meta">SKU: {priceTagData.product.sku}</div>
                )}

                {priceTagData.product.size && (
                  <div className="pt-meta">Size: {priceTagData.product.size}</div>
                )}

                <div className="pt-price">
                  {priceTagData.product.price_tag.final_price}
                </div>

                <div className="pt-barcode-text">
                  {priceTagData.product.price_tag.barcode}
                </div>

                <svg id="product-barcode"></svg>
              </div>
            </div>
          )}

          {/* ================= VARIANT TAGS ================= */}
          {priceTagData?.variants?.map(v => (
            <div key={v.variant_id} className="price-tag-card variant mb-2">
              <div className="price-tag-header ">
                VARIANT – {v.name}
              </div>

              <div className="price-tag-body">
                {v.sku && <div className="pt-meta">SKU: {v.sku}</div>}
                {v.size && <div className="pt-meta">Size: {v.size}</div>}
                <div className="pt-name">{priceTagData.product.name}</div>
                <div className="pt-price">
                  {v.price_tag.final_price}
                </div>

                <div className="pt-barcode-text">
                  {v.price_tag.barcode}
                </div>

                <svg id={`variant-barcode-${v.variant_id}`}></svg>
              </div>
            </div>
          ))}

        </ModalBody>

        <ModalFooter>
          <Button disabled={loading} color="light" onClick={() => setPriceTagOpen(false)}>
            Close
          </Button>
          {/* <Button disabled={loading} color="success" onClick={handlePrint}>
            Print
          </Button> */}
        </ModalFooter>
      </Modal>

      <div id="print-root" style={{ display: "none" }}></div>
    </>
  );
};

export default ProductList;
